import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Building2, TrendingUp, Users } from "lucide-react";

const reveal = (delay: number) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
});

/**
 * Hero mockup collage — three layered fake-but-plausible product UI cards
 * built entirely with HTML/SVG so they animate, scale, and stay crisp.
 *
 * Cards (top→bottom in z-order):
 *  - Property listing card
 *  - Financing match card
 *  - Team avatar cluster card
 */
export const HeroMockup = () => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [40, -40]);

  return (
    <div ref={ref} className="relative w-full h-[520px] md:h-[600px]">
      {/* Soft ochre glow under stack */}
      <div className="glow-ochre absolute inset-0 -z-0" aria-hidden="true" />
      <div className="glow-forest absolute -inset-12 -z-10" aria-hidden="true" />

      <motion.div style={{ y }} className="absolute inset-0">
        {/* ── Property card (back-left) ───────────────────────── */}
        <motion.div
          {...reveal(0)}
          className="absolute top-2 left-0 w-[260px] md:w-[300px] animate-float-a"
        >
          <div className="bento-card overflow-hidden p-0 shadow-brand-md">
            {/* Photo placeholder — ink with blueprint motif */}
            <div className="relative h-[120px] bg-gradient-to-br from-graphite to-ink overflow-hidden">
              <svg
                viewBox="0 0 300 120"
                className="absolute inset-0 w-full h-full text-ochre/40"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                aria-hidden="true"
              >
                <path d="M 0 90 L 60 60 L 60 120 L 0 120 Z" />
                <path d="M 60 60 L 130 30 L 130 120 L 60 120 Z" />
                <path d="M 130 30 L 200 50 L 200 120 L 130 120 Z" />
                <path d="M 200 50 L 300 70 L 300 120 L 200 120 Z" />
                <line x1="80" y1="80" x2="100" y2="80" strokeDasharray="2 3" />
                <line x1="150" y1="60" x2="180" y2="60" strokeDasharray="2 3" />
                <line x1="230" y1="80" x2="270" y2="80" strokeDasharray="2 3" />
              </svg>
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-ochre/90 text-ink text-2xs font-mono font-medium uppercase tracking-wider">
                Distressed
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-stone-400">
                    Brooklyn, NY
                  </p>
                  <p className="font-serif-display text-[15px] font-medium text-ink leading-snug mt-0.5">
                    142 Greene Avenue
                  </p>
                </div>
                <Building2 className="w-3.5 h-3.5 text-stone-400 mt-1" />
              </div>
              <div className="flex items-baseline justify-between pt-1 border-t border-stone-200">
                <span className="text-2xs font-mono uppercase tracking-wider text-stone-400">
                  ARV
                </span>
                <span className="font-mono text-[15px] font-medium text-ink tabular-nums">
                  $2.84M
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Financing match card (mid-right) ────────────────── */}
        <motion.div
          {...reveal(0.15)}
          className="absolute top-[180px] right-0 md:right-[-20px] w-[270px] md:w-[310px] animate-float-b"
        >
          <div className="bento-card p-4 shadow-brand-md space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono uppercase tracking-wider text-stone-400">
                Financing Match
              </p>
              <TrendingUp className="w-3.5 h-3.5 text-ochre" />
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-forest text-bone flex items-center justify-center font-serif-display text-[13px] font-medium">
                MC
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-ink truncate">Meridian Capital</p>
                <p className="text-[11px] text-stone-600">Bridge — 18 mo term</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[13px] font-medium text-ink tabular-nums">7.25%</p>
                <p className="text-2xs font-mono text-stone-400">SOFR + 285</p>
              </div>
            </div>
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between text-2xs font-mono uppercase tracking-wider">
                <span className="text-stone-400">Match Score</span>
                <span className="text-ink font-medium tabular-nums">94</span>
              </div>
              <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "94%" }}
                  transition={{ duration: 1.4, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full rounded-full bg-ochre"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Team cluster card (front-bottom) ────────────────── */}
        <motion.div
          {...reveal(0.3)}
          className="absolute bottom-0 left-6 md:left-12 w-[280px] md:w-[320px] animate-float-c"
        >
          <div className="bento-card p-4 shadow-brand-lg space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono uppercase tracking-wider text-stone-400">
                Deal Team
              </p>
              <Users className="w-3.5 h-3.5 text-stone-400" />
            </div>
            <div className="flex items-center">
              {["AC", "RM", "JL", "SP"].map((init, i) => (
                <div
                  key={init}
                  className="w-8 h-8 rounded-full border-2 border-paper flex items-center justify-center font-serif-display text-[11px] font-medium"
                  style={{
                    marginLeft: i === 0 ? 0 : "-10px",
                    backgroundColor: ["hsl(154 39% 20%)", "hsl(35 53% 56%)", "hsl(140 13% 5%)", "hsl(120 2% 36%)"][i],
                    color: i === 1 ? "hsl(140 13% 5%)" : "hsl(36 33% 94%)",
                    zIndex: 4 - i,
                  }}
                >
                  {init}
                </div>
              ))}
              <div
                className="w-8 h-8 rounded-full border-2 border-paper bg-stone-200 flex items-center justify-center font-mono text-2xs text-stone-600"
                style={{ marginLeft: "-10px" }}
              >
                +3
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {["Contractor", "Architect", "Lender", "Co-investor"].map((role) => (
                <span
                  key={role}
                  className="text-2xs font-mono px-2 py-0.5 rounded-full bg-bone border border-stone-200 text-stone-600"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
